import { create } from "zustand";
import { SolanaWalletId } from "types/solanaWallet";

const STORAGE_KEY = "solanaWallet";

type PersistedSolanaWallet = {
  address: string | null;
  walletId: SolanaWalletId | null;
};

type SolanaWalletStore = PersistedSolanaWallet & {
  setWallet: (wallet: PersistedSolanaWallet) => void;
  resetWallet: () => void;
};

const getInitialState = (): PersistedSolanaWallet => {
  if (typeof window === "undefined") {
    return {
      address: null,
      walletId: null,
    };
  }

  try {
    const rawState = localStorage.getItem(STORAGE_KEY);
    if (!rawState) {
      return {
        address: null,
        walletId: null,
      };
    }

    const parsedState = JSON.parse(rawState) as PersistedSolanaWallet;

    return {
      address: parsedState?.address ?? null,
      walletId: parsedState?.walletId ?? null,
    };
  } catch (error) {
    console.warn("Failed to read persisted solana wallet state", error);
    return {
      address: null,
      walletId: null,
    };
  }
};

const persistState = (state: PersistedSolanaWallet) => {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn("Failed to persist solana wallet state", error);
  }
};

const initialState = getInitialState();

export const solanaWalletStore = create<SolanaWalletStore>((set) => ({
  ...initialState,
  setWallet: (wallet) => {
    persistState(wallet);
    set(wallet);
  },
  resetWallet: () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(STORAGE_KEY);
    }

    set({
      address: null,
      walletId: null,
    });
  },
}));

export const useSolanaWalletStore = solanaWalletStore;
